import { humanoidFace, humanoidSkull } from './humanoid-head.js';
import type { HumanFace, HumanRigId, HumanSkull } from './human.js';
import { NOSE_SHAPE_DEFAULT, type NoseShape } from './humanoid-nose.js';

/** Celestial facial proportions in the shared skull coordinate space: noble, symmetrical, calm. */
export interface CelestialFaceMorph extends NoseShape {
  jawWidth: number; cheekWidth: number; cheek: number; hollow: number; brow: number;
  noseWidth: number; noseLength: number; noseProjection: number; noseRootHeight: number; noseBridge: number;
  mouthWidth: number; lipFullness: number; eyeSize: number; eyeDepth: number;
  /** Forward offset of the eye-level cross-section from the matching Human skull. */
  eyeLevelCentreOffset: number;
  /** Orbital plane before the Eye depth inset; the morph control shows the resulting skull plane. */
  eyePlane: number; eyeOpening: number; eyeTilt: number;
  earLength: number; earWidth: number;
}
export const CELESTIAL_FACE_DEFAULT: CelestialFaceMorph = {
  ...NOSE_SHAPE_DEFAULT, bridgeWidth: .9, wingWidth: .92,
  jawWidth: 1, cheekWidth: 1.02, cheek: .02, hollow: .006, brow: .002,
  noseWidth: .88, noseLength: .041, noseProjection: .098, noseRootHeight: 1.866, noseBridge: -.001,
  mouthWidth: .024, lipFullness: 1.05, eyeSize: 1.06, eyeLevelCentreOffset: 0, eyePlane: .0815, eyeDepth: .006, eyeOpening: .9, eyeTilt: .02,
  earLength: .026, earWidth: .016,
};
export const celestialFaces: { id: string; name: string; morph?: Partial<CelestialFaceMorph> }[] = [
  { id: 'standard', name: 'Celestial classic' },
  { id: 'angular', name: 'Sculpted', morph: { cheekWidth: 1.07, cheek: .024, jawWidth: .97, hollow: .01 } },
  { id: 'soft', name: 'Serene', morph: { jawWidth: 1.03, hollow: .002, lipFullness: 1.14, eyeOpening: .94 } },
  { id: 'aquiline', name: 'Aquiline', morph: { noseLength: .045, noseProjection: .104, noseBridge: .003 } },
  { id: 'stern', name: 'Stern', morph: { brow: .006, eyeDepth: .008, eyeOpening: .82, jawWidth: 1.04, mouthWidth: .026 } },
  { id: 'youthful', name: 'Youthful', morph: { noseLength: .036, eyeSize: 1.12, eyeOpening: .97, lipFullness: 1.1, hollow: .003 } },
];
export function resolveCelestialFace(base: CelestialFaceMorph, id = 'standard'): CelestialFaceMorph {
  return { ...base, ...celestialFaces.find(face => face.id === id)?.morph };
}
export function celestialSkull(m: CelestialFaceMorph, body: HumanRigId): HumanSkull {
  const skull = humanoidSkull(body, 0.98 * m.jawWidth, 1.0 * m.cheekWidth);
  skull.jaw.centre[5] += m.eyeLevelCentreOffset;
  return {
    ...skull,
    eye: { x: .032, y: 1.858, plane: m.eyePlane - m.eyeDepth * .5, opening: m.eyeOpening },
    ear: { ...skull.ear, length: m.earLength, width: m.earWidth, thickness: .006 },
    mouth: { y: 1.794, width: m.mouthWidth, upper: 1, lower: 1, forward: 1, bow: 1.15, corners: .0012, groove: .0005, overlap: .0008, lift: .0005 },
    nose: { rootY: m.noseRootHeight, length: m.noseLength, tipZ: m.noseProjection,
      ...Object.fromEntries(Object.keys(NOSE_SHAPE_DEFAULT).map(key => [key, m[key as keyof NoseShape]])) },
  };
}
export function celestialFaceShape(m: CelestialFaceMorph, body: HumanRigId = 'human-male'): HumanFace {
  return { ...humanoidFace(body, { jaw: .98, chin: .95, square: .9 }, { chinDepth: .001 }), brow: m.brow, cheek: m.cheek, hollow: m.hollow, eye: m.eyeDepth, eyeSize: m.eyeSize,
    nose: m.noseWidth, lips: m.lipFullness, noseLength: 0, bridge: m.noseBridge, fullness: .001,
    eyeTilt: m.eyeTilt, fold: 0 };
}
